/**
 * Notification Actions (Svelte 5 Runes Pattern)
 *
 * API-calling functions for notifications.
 * Pure functions that make API calls and return data.
 * No state management - that belongs in notification.state.svelte.ts
 */

import { apiClient } from '../api/client';
import { apiPath } from '../api/pathBuilder';
import { API_CONFIG } from '$lib/config/apiConfig';
import { createLogger } from '$lib/utils/logger';
import type {
	Notification,
	NotificationPreference,
	GetNotificationsRequest,
	UpdateNotificationPreferenceRequest,
	UnreadCountResponse,
	MarkAllAsReadResponse
} from '$lib/types/notifications';
import type { WhatsNewData } from '$lib/types/whatsNew.types';

const log = createLogger('notification.actions');

/**
 * Combined init payload returned by the backend /notifications/init endpoint.
 * Matches NotificationsInitResponse in app/api/routes/notifications.py.
 *
 * `failed_sections` lists any slot whose sub-query threw server-side;
 * the caller should use the rest of the payload normally and optionally
 * retry the failed slot individually via its dedicated endpoint.
 */
export interface NotificationsInitResponse {
	whats_new: WhatsNewData | null;
	notifications: Notification[];
	unread_count: number;
	preferences: NotificationPreference[];
	generated_at: string;
	failed_sections: string[];
}

/**
 * Fetch the combined init payload in a single HTTP call.
 *
 * Stability: always returns a (possibly partial) body on success, never
 * throws for partial-backend failures — the backend wraps each sub-query
 * in asyncio.gather(return_exceptions=True) and reports failures via
 * `failed_sections`. A hard throw here means the whole endpoint is down,
 * in which case callers should fall back to the legacy endpoints.
 */
export async function fetchNotificationsInit(): Promise<NotificationsInitResponse> {
	try {
		const data = await apiClient.get<NotificationsInitResponse>(
			apiPath.path('/notifications/init')
		);
		if (data.failed_sections && data.failed_sections.length > 0) {
			log.warn('Notifications init returned partial data', {
				failed: data.failed_sections
			});
		} else {
			log.debug('Notifications init loaded', {
				notifications: data.notifications?.length ?? 0,
				unread: data.unread_count,
				preferences: data.preferences?.length ?? 0,
				has_whats_new: !!data.whats_new
			});
		}
		return data;
	} catch (error) {
		log.error('Failed to fetch notifications init', { error });
		throw error;
	}
}

/**
 * Fetch notifications from API
 */
export async function fetchNotifications(
	request?: GetNotificationsRequest
): Promise<Notification[]> {
	try {
		log.debug('Fetching notifications', { request });
		const notifications = await apiClient.get<Notification[]>(
			API_CONFIG.NOTIFICATIONS.LIST(),
			request
		);
		return notifications;
	} catch (error) {
		log.error('Failed to fetch notifications', { error });
		throw error;
	}
}

/**
 * Fetch unread count from API
 */
export async function fetchUnreadCount(): Promise<number> {
	try {
		const response = await apiClient.get<UnreadCountResponse>(
			API_CONFIG.NOTIFICATIONS.UNREAD_COUNT()
		);
		log.debug('Unread count fetched', { response });
		return response.unread_count || 0;
	} catch (error) {
		log.error('Failed to fetch unread count', { error });
		throw error;
	}
}

/**
 * Mark notification as read via API
 */
export async function markNotificationAsRead(notificationId: string): Promise<boolean> {
	try {
		await apiClient.put<void>(API_CONFIG.NOTIFICATIONS.MARK_READ(notificationId));
		log.debug('Notification marked as read', { notificationId });
		return true;
	} catch (error) {
		log.error('Failed to mark notification as read', { error });
		return false;
	}
}

/**
 * Dismiss notification via API
 */
export async function dismissNotificationById(notificationId: string): Promise<boolean> {
	try {
		await apiClient.put<void>(API_CONFIG.NOTIFICATIONS.DISMISS(notificationId));
		log.debug('Notification dismissed', { notificationId });
		return true;
	} catch (error) {
		log.error('Failed to dismiss notification', { error });
		return false;
	}
}

/**
 * Mark all notifications as read via API
 */
export async function markAllNotificationsAsRead(beforeDate?: string): Promise<number> {
	try {
		// Build URL with query parameter if beforeDate is provided
		let url = API_CONFIG.NOTIFICATIONS.MARK_ALL_READ();
		if (beforeDate) {
			url += `?before_date=${encodeURIComponent(beforeDate)}`;
		}

		const response = await apiClient.put<MarkAllAsReadResponse>(url);
		log.debug('Marked all notifications as read', { response });
		return response.marked_count || 0;
	} catch (error) {
		log.error('Failed to mark all as read', { error });
		return 0;
	}
}

/**
 * Fetch notification preferences from API
 */
export async function fetchPreferences(): Promise<NotificationPreference[]> {
	try {
		const preferences = await apiClient.get<NotificationPreference[]>(
			API_CONFIG.NOTIFICATIONS.PREFERENCES.LIST()
		);
		log.debug('Preferences fetched', { count: preferences.length });
		return preferences;
	} catch (error) {
		log.error('Failed to fetch preferences', { error });
		throw error;
	}
}

/**
 * Update notification preference via API
 */
export async function updateNotificationPreference(
	typeId: string,
	request: UpdateNotificationPreferenceRequest
): Promise<boolean> {
	try {
		await apiClient.put<void>(API_CONFIG.NOTIFICATIONS.PREFERENCES.UPDATE(typeId), request);
		log.debug('Preference updated', { typeId });
		return true;
	} catch (error) {
		log.error('Failed to update preference', { error });
		return false;
	}
}
